import React, { useState, useCallback, useMemo, useRef } from 'react'
import { sortBy } from 'lodash'
import InfiniteScroll from 'react-infinite-scroller'
import EmbedConversationsApi from '../../api/embed/ConversationsApi'
import EmbedCard from './EmbedCard'
import CardLoadingAnimation from '../shared/CardLoadingAnimation'
import EmbedLayout, { useEmbedParams } from './EmbedLayout'
import { enrichConversations } from './conversationHelpers'
import type { ConversationDisplayData, ConversationApiData, ApiResponse } from './types'

const PAGE_SIZE = 9

const EmbedConversations: React.FC = () => {
  const { maxItems, partnerIds } = useEmbedParams()
  const [items, setItems] = useState<ConversationDisplayData[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const loadingRef = useRef(false)

  /**
   * useEmbedParams() parses the query string on every render, so `partnerIds`
   * is a new array object each time even when the URL has not changed. Listing
   * that array in the useCallback deps below re-creates handleLoadMore every
   * render; InfiniteScroll sees a new loadMore and calls it again; that sets
   * state, which renders again -- the embed refetches for as long as it is
   * open. It is the same defect documented on EmbedPartner.tsx's pagination,
   * reached by a different route. Depend on the joined string, which is stable
   * by value, and rebuild the array from it.
   */
  const partnerIdsKey = partnerIds.join(',')
  const stablePartnerIds = useMemo(
    () => (partnerIdsKey ? partnerIdsKey.split(',').map(Number) : []),
    [partnerIdsKey]
  )

  const handleLoadMore = useCallback(() => {
    if (loadingRef.current) return
    if (maxItems && items.length >= maxItems) {
      setHasMore(false)
      return
    }
    loadingRef.current = true
    setLoading(true)

    const offset = items.length

    EmbedConversationsApi.getAll({ limit: PAGE_SIZE, offset, partnerIds: stablePartnerIds })
      .then((res: ApiResponse<ConversationApiData[]>) => {
        // A failed request has to stop the scroller, not just return.
        // api/utils.ts `get()` swallows network errors and resolves with the
        // error object, so `res.response` is undefined on this path; the early
        // return then left hasMore true and items.length at 0, InfiniteScroll
        // called loadMore again immediately, and the embed hammered the API in
        // a tight loop for as long as the page was open. Observed live: 13
        // identical requests in 8 seconds against a CORS-refused endpoint.
        // Pre-existing on master, and harmless only while the feed never fails
        // -- which is not a property an embed on someone else's website has.
        if (!res.response?.ok) {
          setHasMore(false)
          return
        }
        const newRaw = res.json || []
        if (newRaw.length < PAGE_SIZE) setHasMore(false)

        const enriched = enrichConversations(newRaw)

        setItems((prev) => {
          const existingIds = new Set(prev.map((c) => c.id))
          const unique = enriched.filter((c) => !existingIds.has(c.id))
          let merged = [...prev, ...unique]
          merged = sortBy(merged, [(o) => o.startsAt])
          if (maxItems) merged = merged.slice(0, maxItems)
          return merged
        })
      })
      .finally(() => {
        loadingRef.current = false
        setLoading(false)
      })
  }, [items.length, maxItems, stablePartnerIds])

  return (
    <EmbedLayout>
      <InfiniteScroll loadMore={handleLoadMore} hasMore={hasMore && !loading} threshold={1000}>
        {items.length === 0 && !loading ? (
          <div className="embed-empty">No conversations available</div>
        ) : (
          <div className="embed-card-grid">
            {items.map((item) => (
              <EmbedCard key={item.id} conversation={item} />
            ))}
          </div>
        )}
        {loading && <CardLoadingAnimation />}
      </InfiniteScroll>
    </EmbedLayout>
  )
}

export default EmbedConversations
